/**
 * PlanDimensions — 2D dimension overlay for plan view.
 *
 * Handles two selection kinds:
 *   • equipment        — draws 4 W/E/N/S clearance arrows using
 *                         `computeClearances` from r3d/scene.js
 *   • tray (drop or run) — draws perpendicular clearances:
 *                         - drop: 4 walls (like equipment)
 *                         - axis-aligned run: 2 perpendicular sides + 2 ends
 *
 * Every dimension label is **click-to-edit**: clicking the label opens a
 * tiny inline number input. Submitting (Enter or ✓) calls `onEditDimension`
 * with `{side, currentDist, newDist}`; the parent (SizeTab) translates that
 * into a position update on the underlying item / tray.
 */
import { useState } from "react";
import { computeClearances, DIM_COLORS } from "@/components/r3d/scene";
import { orientedDimensions } from "@/lib/orientation";
import { fmtLenShort } from "@/lib/units";

function DimArrow({ A, B, label, color, target, side, onEdit, onPreview }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  /* v1.77 — stopAll halts BOTH React synthetic AND native bubbling. The
   * counter widget is rendered as a sibling <g> after the equipment <g>,
   * but the outer <svg> still owns onMouseMove / onMouseUp. Aggressive
   * propagation-stop here PLUS a `data-noequip-drag` guard in PlanView's
   * equipment onMouseDown together kill the recurring "counter drags
   * the rect" bug across all 4 prior failed attempts. */
  const stopAll = (e) => {
    e.stopPropagation();
    if (e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === "function") {
      e.nativeEvent.stopImmediatePropagation();
    }
  };
  // dist=0 (or near-zero floats that round to 0) — collapse the arrow to a
  // zero-length stub anchored at A so the click-to-edit label is still
  // reachable. Without this the user can't dial up clearance from 0.
  const isZero = Math.abs(A.x - B.x) < 0.5 && Math.abs(A.y - B.y) < 0.5;
  const midX = (A.x + B.x) / 2;
  const midY = (A.y + B.y) / 2;
  const tickSize = 6;
  // v1.74 — animate dimension arrow endpoints so the counter visuals
  // glide WITH the equipment rect during programmatic moves (counter
  // edit, undo, quick-orient). Each <line> in SVG supports x1/y1/x2/y2
  // transitions in modern browsers.
  const animMs = "320ms";
  const animEase = "cubic-bezier(0.22, 1, 0.36, 1)";
  const dimAnim = {
    transition: `x1 ${animMs} ${animEase}, y1 ${animMs} ${animEase}, x2 ${animMs} ${animEase}, y2 ${animMs} ${animEase}`,
  };
  const labelAnim = {
    transition: `x ${animMs} ${animEase}, y ${animMs} ${animEase}`,
  };
  const submit = () => {
    const n = parseFloat(val);
    if (Number.isFinite(n) && n >= 0) onEdit && onEdit(side, n);
    onPreview && onPreview(side, null);   // clear live-preview
    setEditing(false);
    setVal("");
  };
  /* v1.81 — live-preview: each keystroke that yields a non-negative number
   * pipes upward via `onPreview` so the equipment / tray glides in the 2D
   * plan view BEFORE the user commits with Enter/✓. Submitting clears the
   * preview and applies the real position; Escape / blur-with-empty also
   * clears so the rect snaps back to actual. */
  const livePreview = (raw) => {
    setVal(raw);
    if (!onPreview) return;
    if (raw === "" || raw == null) { onPreview(side, null); return; }
    const n = parseFloat(raw);
    if (Number.isFinite(n) && n >= 0) onPreview(side, n);
    else onPreview(side, null);
  };
  return (
    <g>
      {!isZero && (
        <>
          <line x1={A.x} y1={A.y} x2={B.x} y2={B.y} style={dimAnim}
                stroke={color} strokeWidth="1.4" strokeDasharray="4 2" />
          <line x1={A.x - tickSize} y1={A.y - tickSize}
                x2={A.x + tickSize} y2={A.y + tickSize} style={dimAnim}
                stroke={color} strokeWidth="1.5" />
          <line x1={A.x + tickSize} y1={A.y - tickSize}
                x2={A.x - tickSize} y2={A.y + tickSize} style={dimAnim}
                stroke={color} strokeWidth="1.5" />
          <line x1={B.x - tickSize} y1={B.y - tickSize}
                x2={B.x + tickSize} y2={B.y + tickSize} style={dimAnim}
                stroke={color} strokeWidth="1.5" />
          <line x1={B.x + tickSize} y1={B.y - tickSize}
                x2={B.x - tickSize} y2={B.y + tickSize} style={dimAnim}
                stroke={color} strokeWidth="1.5" />
        </>
      )}
      {isZero && (
        <circle cx={A.x} cy={A.y} r="3" fill={color} style={{ transition: `cx ${animMs} ${animEase}, cy ${animMs} ${animEase}` }} />
      )}
      {!editing ? (
        <g style={{ cursor: onEdit ? "text" : "default" }}
           onMouseDown={stopAll}
           onPointerDown={stopAll}
           onTouchStart={stopAll}
           onMouseUp={stopAll}
           onPointerUp={stopAll}
           onTouchEnd={stopAll}
           onClick={(e) => {
             e.stopPropagation();
             if (onEdit) { setEditing(true); setVal(""); }
           }}
           data-noequip-drag="true"
           data-testid={`dim-label-${side}`}>
          <rect x={midX - 36} y={midY - 9} width="72" height="16" style={labelAnim}
                fill="rgba(10,13,18,0.95)" stroke={color} strokeWidth="0.8" />
          <text x={midX} y={midY + 3} fill={color} style={labelAnim}
                fontFamily="JetBrains Mono" fontSize="10" textAnchor="middle"
                fontWeight="700" pointerEvents="none">
            {label}
          </text>
          {target && target !== "wall" && target !== "ceiling" && target !== "floor" && (
            <text x={midX} y={midY + 14} fill={color} style={labelAnim}
                  fontFamily="JetBrains Mono" fontSize="8"
                  textAnchor="middle" opacity="0.85" pointerEvents="none">
              → {target}
            </text>
          )}
        </g>
      ) : (
        <foreignObject x={midX - 40} y={midY - 11} width={80} height={22}
                        onMouseDown={stopAll}
                        onPointerDown={stopAll}
                        onTouchStart={stopAll}
                        onMouseUp={stopAll}
                        onPointerUp={stopAll}
                        onTouchEnd={stopAll}
                        onClick={(e) => e.stopPropagation()}
                        data-noequip-drag="true">
          <div onMouseDown={stopAll}
               onPointerDown={stopAll}
               onTouchStart={stopAll}
               style={{
                 display: "flex", gap: 2,
                 border: `1px solid ${color}`, background: "rgba(10,13,18,0.95)",
                 padding: "1px 2px", height: 20,
                 fontFamily: "JetBrains Mono", fontSize: 10,
               }}>
            <input autoFocus type="number" value={val} placeholder="in″"
                   onMouseDown={stopAll}
                   onPointerDown={stopAll}
                   onTouchStart={stopAll}
                   onClick={(e) => e.stopPropagation()}
                   onChange={(e) => livePreview(e.target.value)}
                   onBlur={() => {
                     // commit on blur if the user typed something, otherwise
                     // silently dismiss — matches industrial-design CAD UX.
                     if (val !== "" && Number.isFinite(parseFloat(val))) submit();
                     else {
                       onPreview && onPreview(side, null);
                       setEditing(false); setVal("");
                     }
                   }}
                   onKeyDown={(e) => {
                     if (e.key === "Enter") submit();
                     if (e.key === "Escape") {
                       onPreview && onPreview(side, null);
                       setEditing(false); setVal("");
                     }
                   }}
                   style={{
                     width: "100%", background: "transparent",
                     color, border: "none", outline: "none",
                     fontFamily: "inherit", fontSize: "inherit",
                   }}
                   data-testid={`dim-input-${side}`} />
            <button type="button"
                    onMouseDown={stopAll}
                    onPointerDown={stopAll}
                    onTouchStart={stopAll}
                    onClick={(e) => { e.stopPropagation(); submit(); }}
                    style={{
                      background: "transparent", border: "none",
                      color, cursor: "pointer", fontWeight: 700,
                    }}>✓</button>
          </div>
        </foreignObject>
      )}
    </g>
  );
}

function equipmentDims({ item, room, allItems, toSvg, project }) {
  const cl = computeClearances(item, room, allItems);
  if (!cl) return [];
  const p = item.position;
  // v1.71 — use orientedDimensions so the bbox reflects the CURRENT
  // axis_map (post-flip). pos.w_in / pos.h_in lag behind the local state
  // until the backend autosave round-trip syncs them.
  const _od = orientedDimensions(item);
  const w = _od.wallSize;
  const d = _od.thickness;
  const cx = p.x_in + w / 2;
  const cy = p.y_in + d / 2;
  return [
    { side: "W", color: DIM_COLORS.W, target: cl.W.target, currentDist: cl.W.dist,
      label: `← ${fmtLenShort(cl.W.dist, project)}`,
      A: toSvg(p.x_in, cy), B: toSvg(p.x_in - cl.W.dist, cy) },
    { side: "E", color: DIM_COLORS.E, target: cl.E.target, currentDist: cl.E.dist,
      label: `${fmtLenShort(cl.E.dist, project)} →`,
      A: toSvg(p.x_in + w, cy), B: toSvg(p.x_in + w + cl.E.dist, cy) },
    { side: "N", color: DIM_COLORS.N, target: cl.N.target, currentDist: cl.N.dist,
      label: `↓ ${fmtLenShort(cl.N.dist, project)}`,
      A: toSvg(cx, p.y_in), B: toSvg(cx, p.y_in - cl.N.dist) },
    { side: "S", color: DIM_COLORS.S, target: cl.S.target, currentDist: cl.S.dist,
      label: `↑ ${fmtLenShort(cl.S.dist, project)}`,
      A: toSvg(cx, p.y_in + d), B: toSvg(cx, p.y_in + d + cl.S.dist) },
  ];
}

/** Tray-run dims: perpendicular clearances to walls + nearest parallel tray
 *  on each side. Only handles axis-aligned runs (the common case).         */
function trayRunDims({ tray, room, allTrays, toSvg, project }) {
  const pts = tray.points || [];
  if (pts.length < 2) return [];
  const [a, b] = pts;
  const isHorizontal = Math.abs(a[1] - b[1]) < 1;
  const isVertical   = Math.abs(a[0] - b[0]) < 1;
  if (!isHorizontal && !isVertical) return [];     // diagonal trays — skip dims
  const L = room.length_in, W = room.width_in;
  if (isHorizontal) {
    const y = a[1];
    const minX = Math.min(a[0], b[0]);
    const maxX = Math.max(a[0], b[0]);
    const midX = (minX + maxX) / 2;
    // Nearest parallel-horizontal trays above & below.
    let aboveY = 0,       aboveTag = "wall";
    let belowY = W,       belowTag = "wall";
    allTrays.forEach((t) => {
      if (t.id === tray.id || t.kind === "drop") return;
      if (!Array.isArray(t.points) || t.points.length < 2) return;
      if (Math.abs(t.points[0][1] - t.points[1][1]) > 1) return;
      const ty = t.points[0][1];
      // Only count trays whose X-range overlaps ours.
      const tMinX = Math.min(t.points[0][0], t.points[1][0]);
      const tMaxX = Math.max(t.points[0][0], t.points[1][0]);
      const overlaps = !(tMaxX < minX || tMinX > maxX);
      if (!overlaps) return;
      if (ty < y && ty > aboveY) { aboveY = ty; aboveTag = t.id_display || t.tag || t.id?.slice(0, 6); }
      if (ty > y && ty < belowY) { belowY = ty; belowTag = t.id_display || t.tag || t.id?.slice(0, 6); }
    });
    return [
      { side: "N", color: DIM_COLORS.N, target: aboveTag, currentDist: y - aboveY,
        label: `↓ ${fmtLenShort(y - aboveY, project)}`,
        A: toSvg(midX, y), B: toSvg(midX, aboveY) },
      { side: "S", color: DIM_COLORS.S, target: belowTag, currentDist: belowY - y,
        label: `↑ ${fmtLenShort(belowY - y, project)}`,
        A: toSvg(midX, y), B: toSvg(midX, belowY) },
      { side: "W", color: DIM_COLORS.W, target: "wall", currentDist: minX,
        label: `← ${fmtLenShort(minX, project)}`,
        A: toSvg(minX, y), B: toSvg(0, y) },
      { side: "E", color: DIM_COLORS.E, target: "wall", currentDist: L - maxX,
        label: `${fmtLenShort(L - maxX, project)} →`,
        A: toSvg(maxX, y), B: toSvg(L, y) },
    ];
  }
  // Vertical run
  const x = a[0];
  const minY = Math.min(a[1], b[1]);
  const maxY = Math.max(a[1], b[1]);
  const midY = (minY + maxY) / 2;
  let leftX = 0,       leftTag = "wall";
  let rightX = L,      rightTag = "wall";
  allTrays.forEach((t) => {
    if (t.id === tray.id || t.kind === "drop") return;
    if (!Array.isArray(t.points) || t.points.length < 2) return;
    if (Math.abs(t.points[0][0] - t.points[1][0]) > 1) return;
    const tx = t.points[0][0];
    const tMinY = Math.min(t.points[0][1], t.points[1][1]);
    const tMaxY = Math.max(t.points[0][1], t.points[1][1]);
    const overlaps = !(tMaxY < minY || tMinY > maxY);
    if (!overlaps) return;
    if (tx < x && tx > leftX)  { leftX  = tx; leftTag  = t.id_display || t.tag || t.id?.slice(0, 6); }
    if (tx > x && tx < rightX) { rightX = tx; rightTag = t.id_display || t.tag || t.id?.slice(0, 6); }
  });
  return [
    { side: "W", color: DIM_COLORS.W, target: leftTag,  currentDist: x - leftX,
      label: `← ${fmtLenShort(x - leftX, project)}`,
      A: toSvg(x, midY), B: toSvg(leftX, midY) },
    { side: "E", color: DIM_COLORS.E, target: rightTag, currentDist: rightX - x,
      label: `${fmtLenShort(rightX - x, project)} →`,
      A: toSvg(x, midY), B: toSvg(rightX, midY) },
    { side: "N", color: DIM_COLORS.N, target: "wall", currentDist: minY,
      label: `↓ ${fmtLenShort(minY, project)}`,
      A: toSvg(x, minY), B: toSvg(x, 0) },
    { side: "S", color: DIM_COLORS.S, target: "wall", currentDist: W - maxY,
      label: `↑ ${fmtLenShort(W - maxY, project)}`,
      A: toSvg(x, maxY), B: toSvg(x, W) },
  ];
}

/** Tray-drop dims — drops are vertical, so plan view shows just one point.
 *  Treat it like equipment for clearance arrows. */
function trayDropDims({ tray, room, toSvg, project }) {
  const pt = tray.points?.[0];
  if (!pt) return [];
  const [x, y] = pt;
  const L = room.length_in, W = room.width_in;
  return [
    { side: "W", color: DIM_COLORS.W, target: "wall", currentDist: x,
      label: `← ${fmtLenShort(x, project)}`,
      A: toSvg(x, y), B: toSvg(0, y) },
    { side: "E", color: DIM_COLORS.E, target: "wall", currentDist: L - x,
      label: `${fmtLenShort(L - x, project)} →`,
      A: toSvg(x, y), B: toSvg(L, y) },
    { side: "N", color: DIM_COLORS.N, target: "wall", currentDist: y,
      label: `↓ ${fmtLenShort(y, project)}`,
      A: toSvg(x, y), B: toSvg(x, 0) },
    { side: "S", color: DIM_COLORS.S, target: "wall", currentDist: W - y,
      label: `↑ ${fmtLenShort(W - y, project)}`,
      A: toSvg(x, y), B: toSvg(x, W) },
  ];
}

export default function PlanDimensions({
  selectedItem, selectedKind, room, allItems = [], allTrays = [],
  toSvg, onEditDimension, onPreviewDimension, project,
}) {
  if (!selectedItem) return null;
  let dims = [];
  if (selectedKind === "equipment") {
    if (!selectedItem.position) return null;
    dims = equipmentDims({ item: selectedItem, room, allItems, toSvg, project });
  } else if (selectedKind === "tray") {
    if (selectedItem.kind === "drop") {
      dims = trayDropDims({ tray: selectedItem, room, toSvg, project });
    } else {
      dims = trayRunDims({ tray: selectedItem, room, allTrays, toSvg, project });
    }
  }
  return (
    <g pointerEvents="auto" data-testid="plan-dimensions"
       data-noequip-drag="true"
       onMouseDown={(e) => e.stopPropagation()}
       onPointerDown={(e) => e.stopPropagation()}
       onTouchStart={(e) => e.stopPropagation()}
       onMouseUp={(e) => e.stopPropagation()}
       onPointerUp={(e) => e.stopPropagation()}
       onTouchEnd={(e) => e.stopPropagation()}>
      {dims.map((d) => (
        <DimArrow key={d.side}
          A={d.A} B={d.B}
          color={d.color}
          label={d.label}
          target={d.target}
          side={d.side}
          onEdit={onEditDimension
            ? (side, newVal) => onEditDimension({
                side, currentDist: d.currentDist, newDist: newVal,
              })
            : null}
          onPreview={onPreviewDimension
            ? (side, newVal) => onPreviewDimension(
                newVal == null ? null : { side, currentDist: d.currentDist, newDist: newVal },
              )
            : null} />
      ))}
    </g>
  );
}
